import { useState, useEffect } from 'react';
import { usePhoneNumber } from './hooks/usePhoneNumber';
import { Routes, Route, useNavigate, useLocation, Link } from 'react-router-dom';
import { Plane, CheckCircle2 } from 'lucide-react';
import { useTranslation, Trans } from 'react-i18next';
import './App.css';
import MainForm from './components/mainForm';
import Booking from './components/taxista/Booking';
import LoginPage from './components/login/loginForm';
import About from './components/About';
import ExploreBarcelona from './components/ExploreBarcelona';
import Footer from './components/Footer';
import { AuthProvider, useAuth } from './auth/AuthContext';
import ProtectedRoute from './auth/ProtectedRoute';
import { requestNotificationPermissionAndSubscribe } from './notify/permission';
import { subscribeUserToPush, unsubscribeUserToPush } from './notify/subscribe';
import WhatsAppButton from './components/WhatsAppButton';
import ReserveButton from './components/ReserveButton';
import LanguageSelector from './components/LanguageSelector';
import Rates from './components/Rates';
import HowItWorks from './components/HowItWorks';
import FAQ from './components/FAQ';
import Contact from './components/Contact';
import logo from './image/logo/barcelona cab logo.png';
import TrustSignals from './components/TrustSignals';

// --- Public Header ---
function PublicHeader() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    return (
        <nav className="fixed top-0 left-0 right-0 z-50 bg-white/70 backdrop-blur-xl border-b border-white/20 shadow-sm">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between h-20 md:h-24">
                    <div className="flex items-center cursor-pointer group" onClick={() => navigate('/')}>
                        <div className="relative p-1.5 md:p-2 bg-white/80 rounded-2xl shadow-inner border border-white/50 mr-3 md:mr-4 transform transition-transform group-hover:scale-105">
                            <img
                                src={logo}
                                alt="Barcelona Cab Logo"
                                className="w-10 h-10 md:w-14 md:h-14 object-contain"
                            />
                        </div>
                        <div className="flex flex-col">
                            <span className="text-xl md:text-2xl font-black text-slate-900 tracking-tighter leading-none">
                                MY<span className="text-blue-600">TAXI</span>BCN
                            </span>
                            <span className="text-[10px] md:text-xs font-bold text-slate-500 uppercase tracking-[0.2em] mt-1">Official Service</span>
                        </div>
                    </div>

                    {/* Desktop Navigation */}
                    <div className="hidden md:flex items-center space-x-10">
                        <Link to="/" className="text-slate-600 hover:text-blue-600 font-bold transition-all text-xs uppercase tracking-[0.15em] border-b-2 border-transparent hover:border-blue-600 pb-1">{t('navbar.home')}</Link>
                        <Link to="/explore" className="text-slate-600 hover:text-blue-600 font-bold transition-all text-xs uppercase tracking-[0.15em] border-b-2 border-transparent hover:border-blue-600 pb-1">{t('navbar.explore')}</Link>
                        <Link to="/about" className="text-slate-600 hover:text-blue-600 font-bold transition-all text-xs uppercase tracking-[0.15em] border-b-2 border-transparent hover:border-blue-600 pb-1">{t('navbar.about')}</Link>
                        <div className="h-8 w-[1px] bg-slate-200"></div>
                        <LanguageSelector />
                    </div>

                    {/* Mobile Burger Button */}
                    <div className="md:hidden flex items-center gap-3">
                        <LanguageSelector />
                        <button
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            className="text-slate-900 p-2.5 rounded-xl bg-slate-50 hover:bg-slate-100 transition-all border border-slate-200"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                {isMobileMenuOpen ? (
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                                ) : (
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 6h16M4 12h16M4 18h16" />
                                )}
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Mobile Menu */}
                {isMobileMenuOpen && (
                    <div className="md:hidden pb-6 space-y-1 animate-in slide-in-from-top-4 duration-300">
                        <Link
                            to="/"
                            className="block px-4 py-4 text-slate-700 hover:text-blue-600 hover:bg-blue-50/50 font-bold text-sm uppercase tracking-widest transition-all rounded-xl"
                            onClick={() => setIsMobileMenuOpen(false)}
                        >
                            {t('navbar.home')}
                        </Link>
                        <Link
                            to="/explore"
                            className="block px-4 py-4 text-slate-700 hover:text-blue-600 hover:bg-blue-50/50 font-bold text-sm uppercase tracking-widest transition-all rounded-xl"
                            onClick={() => setIsMobileMenuOpen(false)}
                        >
                            {t('navbar.explore')}
                        </Link>
                        <Link
                            to="/about"
                            className="block px-4 py-4 text-slate-700 hover:text-blue-600 hover:bg-blue-50/50 font-bold text-sm uppercase tracking-widest transition-all rounded-xl"
                            onClick={() => setIsMobileMenuOpen(false)}
                        >
                            {t('navbar.about')}
                        </Link>
                    </div>
                )}
            </div>
        </nav>
    );
}


// --- Navigation Header (Authenticated) ---
const NavigationHeader = () => {
    const { isLoggedIn, handleLogout, username } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    // Notification state - initialize from localStorage
    const [isPushEnabled, setIsPushEnabled] = useState(() => {
        return localStorage.getItem('pushEnabled') === 'true';
    });

    // Sync with actual browser permission on mount
    useEffect(() => {
        if (isPushEnabled && Notification.permission !== 'granted') {
            setIsPushEnabled(false);
            localStorage.setItem('pushEnabled', 'false');
        }
    }, []);

    // Define the paths where the button should be visible
    const showButtonPaths = ['/booking', '/admin'];
    const shouldShowButton = showButtonPaths.includes(location.pathname);


    // Toggle notification logic
    const handleNotificationToggle = async () => {
        if (!isLoggedIn) {
            alert("You must be logged in to manage notifications.");
            return;
        }

        // Ensure we have a username to subscribe with
        if (!username) {
            // console.error("No username found in context.");
            alert("Error: User identity not found. Please re-login.");
            return;
        }

        if (!('Notification' in window) || !('serviceWorker' in navigator) || !('PushManager' in window)) {
            alert("Your browser doesn't support the required notification features.");
            return;
        }

        if (isPushEnabled) {
            // console.log(`Unsubscribing ${username} from push notifications...`);
            const registration = await navigator.serviceWorker.register('/service-worker.js');
            unsubscribeUserToPush(registration, username);
            setIsPushEnabled(false);
            localStorage.setItem('pushEnabled', 'false');
            return;
        }

        try {
            const permission = await Notification.requestPermission();
            if (permission !== 'granted') {
                // console.warn('Notification permission denied by the user.');
                alert('Permission denied. Please enable notifications in your browser settings.');
                return;
            }

            const registration = await navigator.serviceWorker.register('/service-worker.js');
            // console.log(`Subscribing ${username} to push notifications...`);
            subscribeUserToPush(registration, username);
            setIsPushEnabled(true);
            localStorage.setItem('pushEnabled', 'true');
            alert("Notifications enabled successfully!");

        } catch (error) {
            // console.error('Subscription failed:', error);
            alert('Failed to enable notifications. See console for details.');
        }
    };

    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    return (
        <nav className="bg-[#1e293b] shadow-lg sticky top-0 z-50 border-b border-gray-700">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between h-16">
                    <div className="flex items-center">
                        <div className="w-8 h-8 bg-indigo-500 rounded flex items-center justify-center mr-3 text-white font-bold">A</div>
                        <span className="text-xl font-bold text-white">Admin Portal</span>
                    </div>

                    {/* Desktop Navigation */}
                    <div className="hidden md:flex items-center space-x-4">
                        {shouldShowButton && isLoggedIn && (
                            <button
                                onClick={handleNotificationToggle}
                                className={`px-4 py-2 rounded-md transition-all duration-200 font-semibold text-sm flex items-center gap-2 ${isPushEnabled ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
                            >
                                {isPushEnabled ? '🔔 Alerts ON' : '🔕 Enable Alerts'}
                            </button>
                        )}

                        {isLoggedIn && (
                            <>
                                <span className="text-gray-300 text-sm">
                                    User: <span className="font-bold text-white">{username || 'Admin'}</span>
                                </span>
                                <button
                                    onClick={async () => {
                                        await handleLogout();
                                        navigate('/');
                                    }}
                                    className="bg-red-600 text-white px-4 py-2 rounded-md text-sm font-semibold hover:bg-red-700 transition-all"
                                >
                                    Logout
                                </button>
                            </>
                        )}
                    </div>

                    {/* Mobile Burger Button */}
                    <div className="md:hidden flex items-center">
                        <button
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            className="text-white p-2 rounded-lg hover:bg-gray-700 transition-all"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                {isMobileMenuOpen ? (
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                ) : (
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                )}
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Mobile Menu */}
                {isMobileMenuOpen && (
                    <div className="md:hidden pb-4 space-y-3 animate-fade-in bg-[#1e293b]">
                        {isLoggedIn && (
                            <div className="px-4 py-2 text-gray-300 text-sm border-b border-gray-700">
                                User: <span className="font-bold text-white">{username || 'Admin'}</span>
                            </div>
                        )}

                        {shouldShowButton && isLoggedIn && (
                            <button
                                onClick={() => {
                                    handleNotificationToggle();
                                    setIsMobileMenuOpen(false);
                                }}
                                className={`w-full px-4 py-2 rounded-md transition-all duration-200 font-semibold text-sm flex items-center justify-center gap-2 ${isPushEnabled ? 'bg-green-600 text-white' : 'bg-gray-700 text-gray-300'}`}
                            >
                                {isPushEnabled ? '🔔 Alerts ON' : '🔕 Enable Alerts'}
                            </button>
                        )}

                        {isLoggedIn && (
                            <button
                                onClick={async () => {
                                    await handleLogout();
                                    navigate('/');
                                    setIsMobileMenuOpen(false);
                                }}
                                className="w-full bg-red-600 text-white px-4 py-2 rounded-md text-sm font-semibold hover:bg-red-700 transition-all"
                            >
                                Logout
                            </button>
                        )}
                    </div>
                )}
            </div>
        </nav>
    );
};


// --- Login Page Wrapper ---
const LoginPageWrapper = () => {
    const { handleLogin, isLoggedIn } = useAuth();
    const navigate = useNavigate();

    if (isLoggedIn) {
        return navigate('/booking', { replace: true });
    }

    const onLoginSubmit = async (username, password, setError) => {
        try {
            await handleLogin(username, password);
            navigate('/booking');
            return true;
        } catch (error) {
            setError(error.message);
            return false;
        }
    };

    return <LoginPage onLogin={onLoginSubmit} />;
};


// --- Main App Component ---
function App() {
    const [bookingActive, setBookingActive] = useState(false);
    const location = useLocation();
    const { t } = useTranslation();
    const { phoneNumber } = usePhoneNumber();

    const authenticatedPaths = ['/booking', '/admin'];
    const useAuthenticatedHeader = authenticatedPaths.includes(location.pathname);
    const HeaderComponent = useAuthenticatedHeader ? NavigationHeader : PublicHeader;

    return (
        <div className="App min-h-screen bg-[#f8f9fa] font-sans text-[#282a34] flex flex-col">
            <AuthProvider>
                <HeaderComponent />

                {/* Main Content Area */}
                <div className="flex-grow w-full">
                    {/* Hero Section for Home Page */}
                    {location.pathname === '/' && (
                        <>
                            <section className="relative min-h-screen flex flex-col pb-8 md:pb-16 px-4 overflow-hidden pt-24 bg-slate-900">
                                {/* Background Image */}
                                <div className="absolute inset-0 z-0">
                                    <img
                                        src="/Gemini_Generated_Image_psfafhpsfafhpsfa.png"
                                        alt="Taxi en Aeropuerto Barcelona-El Prat"
                                        className="w-full h-full object-cover"
                                    />
                                    <div className="absolute inset-0 bg-slate-900/60 md:bg-slate-900/50"></div>
                                </div>

                                <div className={`container mx-auto relative z-10 flex flex-col ${bookingActive ? 'items-center' : 'items-center md:items-start'} gap-12 md:gap-16 ${bookingActive ? 'mt-4 mb-auto md:my-auto' : 'mt-auto'} max-w-6xl`}>
                                    {!bookingActive && (
                                        <div className="text-center md:text-left text-white space-y-6 max-w-2xl">
                                            <div className="inline-flex items-center gap-3 px-5 py-2 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 text-white text-xs font-black uppercase tracking-[0.2em] shadow-2xl animate-fade-in-up">
                                                <Plane className="w-4 h-4 text-blue-400" />
                                                <span>{t('home.hero_badge') || "Trusted Airport Transfers"}</span>
                                            </div>
                                            <h1 className="text-4xl md:text-6xl font-black leading-[1.1] tracking-tighter drop-shadow-2xl animate-slide-in-left">
                                                {t('home.hero_title')}
                                            </h1>
                                            <div className="animate-slide-in-left stagger-1">
                                                <p className="text-lg md:text-xl text-gray-100 font-bold leading-relaxed drop-shadow-lg max-w-xl">
                                                    {t('home.hero_subtitle')}
                                                </p>

                                                {/* Trust Points List */}
                                                <div className="flex flex-col gap-3 mt-6 mb-8">
                                                    {[1, 2, 3].map((i) => (
                                                        <div key={i} className="flex items-center gap-3 text-white/90 animate-fade-in-up stagger-2">
                                                            <CheckCircle2 className="w-5 h-5 text-green-400 shrink-0" />
                                                            <span className="text-sm md:text-base font-medium drop-shadow-sm">
                                                                {t(`home.hero_trust_${i}`)}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>

                                                <p className="text-lg md:text-xl text-gray-100 font-medium leading-relaxed drop-shadow-lg max-w-xl mb-10">
                                                    <Trans
                                                        i18nKey="home.hero_contact_cta"
                                                        values={{ phoneNumber }}
                                                        components={[
                                                            <a
                                                                href={`tel:${phoneNumber.replace(/\s/g, '')}`}
                                                                className="inline-flex items-center justify-center gap-2 bg-blue-600 text-white px-6 py-2 rounded-xl font-black text-base hover:bg-blue-700 transition-all shadow-[0_10px_30px_rgba(37,99,235,0.25)] mt-4 no-underline animate-gentle-bounce hover:scale-105 active:scale-95"
                                                            />
                                                        ]}
                                                    />
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    <div className="w-full animate-slide-in-right stagger-2 relative">
                                        <div className="absolute -inset-4 bg-white/5 backdrop-blur-[2px] rounded-[32px] md:-inset-6"></div>
                                        <MainForm onBookingActiveChange={setBookingActive} />
                                    </div>
                                </div>
                            </section>
                            <TrustSignals />
                        </>
                    )}

                    {/* Content Sections for Home Page */}
                    {location.pathname === '/' && (
                        <>
                            <HowItWorks />
                            <FAQ />
                            <Contact />
                        </>
                    )}

                    {/* Content Container - Only for non-home pages */}
                    <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${location.pathname !== '/' ? 'py-8' : ''}`}>
                        <Routes>
                            <Route path="/" element={null} />
                            <Route path="/explore" element={<ExploreBarcelona />} />
                            <Route path="/about" element={<About />} />
                            <Route path="/booking" element={
                                <ProtectedRoute>
                                    <Booking />
                                </ProtectedRoute>
                            } />
                            <Route path="/admin" element={<LoginPageWrapper />} />
                            <Route path="*" element={<div className="text-center py-20"><h1 className="text-4xl font-bold text-gray-300">404 Page Not Found</h1></div>} />
                        </Routes>
                    </div>
                </div>

                <Footer />

                {/* Floating WhatsApp Button (Hidden when booking details are active) */}
                {!bookingActive && <WhatsAppButton />}
                <ReserveButton />
            </AuthProvider>
        </div>
    );
}

export default App;
